import React from 'react';
import { color, radius, shadow, space, visuallyHidden } from '../theme';

/* Grey placeholders shaped like the content that is loading. Static (no shimmer):
   inline styles can't declare keyframes. */

interface SkeletonProps {
  width?: number | string;
  height: number;
  rounded?: number;
  style?: React.CSSProperties;
}

export const Skeleton = ({ width = '100%', height, rounded = radius.sm, style }: SkeletonProps) => (
  <span aria-hidden="true" style={{ display: 'block', width, height, borderRadius: rounded, background: color.skeleton, flexShrink: 0, ...style }} />
);

// Lines of text; the last one is shorter
export const SkeletonText = ({ lines = 2, lineHeight = 12 }: { lines?: number; lineHeight?: number }) => (
  <span style={{ display: 'flex', flexDirection: 'column', gap: space.sm, flex: 1, minWidth: 0 }}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} height={lineHeight} width={i === lines - 1 && lines > 1 ? '60%' : '100%'} />
    ))}
  </span>
);

interface SkeletonListProps {
  rows?: number;
  avatar?: boolean;
  // Wrap the rows in a card, matching a ListRow card
  card?: boolean;
}

// A loading list. Announces "טוען" to screen readers once.
export const SkeletonList = ({ rows = 6, avatar = false, card = true }: SkeletonListProps) => (
  <div role="status" data-skeleton="true" style={card ? { background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, overflow: 'hidden' } : undefined}>
    <span style={visuallyHidden}>טוען</span>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: space.md, minHeight: 56, padding: `${space.sm + 2}px ${space.lg}px`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none', boxSizing: 'border-box' }}>
        {avatar && <Skeleton width={40} height={40} rounded={20} />}
        <SkeletonText lines={2} />
      </div>
    ))}
  </div>
);

// A loading row of stat tiles
export const SkeletonTiles = ({ count = 4 }: { count?: number }) => (
  <div aria-hidden="true" style={{ display: 'flex', gap: space.sm }}>
    {Array.from({ length: count }, (_, i) => <Skeleton key={i} height={60} rounded={radius.lg} style={{ flex: 1 }} />)}
  </div>
);
